import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import itLocale from "@fullcalendar/core/locales/it";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import FormDialog from "../components/FormDialog";
import { ACTIVITY_FIELDS } from "../lib/entities";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CommittenteSelect from "../components/CommittenteSelect";

const TYPE_COLOR = {
  meeting: "#0f766e", "video call": "#0e7490", telefonata: "#1d4ed8", "follow-up": "#b45309", email: "#6d28d9",
  linkedin: "#0369a1", whatsapp: "#15803d", task: "#475569", nota: "#64748b", altro: "#52525b",
};
const TYPES = Object.keys(TYPE_COLOR);
const isAllDay = (a) => !!a.all_day || /T00:00(:00)?/.test(a.due_date || "") && !a.duration;

export default function CalendarPage() {
  const { can } = useAuth();
  const ref = useRef(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [typeFilter, setTypeFilter] = useState("all");
  const [committente, setCommittente] = useState("all");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(null);

  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth < 768;
      if (m !== isMobile) { setIsMobile(m); ref.current?.getApi().changeView(m ? "listWeek" : "timeGridWeek"); }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile]);

  const fetchEvents = useCallback((info, success, failure) => {
    const params = { start: info.start.toISOString(), end: info.end.toISOString() };
    if (typeFilter !== "all") params.type = typeFilter;
    if (committente !== "all") params.committente = committente;
    api.get("/activities/calendar", { params }).then(({ data }) => success(data.map((a) => ({
      id: a.id, title: a.subject || "(senza oggetto)", start: a.due_date, end: a.end_date || undefined, allDay: isAllDay(a),
      backgroundColor: TYPE_COLOR[a.type] || "#475569", borderColor: "transparent",
      classNames: a.status === "completata" ? ["opacity-50", "line-through"] : [], extendedProps: a,
    })))).catch(failure);
  }, [typeFilter, committente]);

  const refetch = () => ref.current?.getApi().refetchEvents();
  const editInitial = useMemo(() => (edit ? { ...edit, recurrence_freq: edit.recurrence?.freq || "none", recurrence_until: edit.recurrence?.until || "" } : {}), [edit]);
  const createInitial = useMemo(() => create || {}, [create]);

  const onMove = async ({ event, revert }) => {
    try {
      await api.put(`/activities/${event.id}`, { due_date: event.start.toISOString(), end_date: event.end ? event.end.toISOString() : null, all_day: event.allDay });
      toast.success("Attività riprogrammata"); refetch();
    } catch (e) { toast.error("Spostamento non riuscito"); revert(); }
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendario" subtitle="Meeting, video call, telefonate, follow-up e task pianificati — vista unica sulle Attività CRM">
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <CommittenteSelect mode="filter" testid="calendar-committente-filter" value={committente} onChange={(v) => { setCommittente(v); setTimeout(refetch, 0); }} className="!h-9 sm:w-52" />
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setTimeout(refetch, 0); }}>
            <SelectTrigger data-testid="calendar-type-filter" className="w-full sm:w-44 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i tipi</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
          </Select>
          {can("write") && <Button data-testid="calendar-add-btn" onClick={() => setCreate({ due_date: new Date().toISOString().slice(0, 16), type: "meeting", duration: 60 })} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Nuovo evento</Button>}
        </div>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-3 text-[11px]" data-testid="calendar-legend">
        {TYPES.map((t) => <span key={t} className="inline-flex items-center gap-1.5 capitalize text-slate-600"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_COLOR[t] }} />{t}</span>)}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-2 sm:p-3 fc-ferranti overflow-x-auto" data-testid="calendar-container">
        <FullCalendar ref={ref} plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]} locale={itLocale} timeZone="local"
          initialView={isMobile ? "listWeek" : "timeGridWeek"} height="auto" nowIndicator weekNumbers={false} firstDay={1} slotMinTime="07:00:00" slotMaxTime="21:00:00"
          headerToolbar={isMobile ? { left: "prev,next today", center: "title", right: "timeGridDay,listWeek,dayGridMonth" } : { left: "prev,next today", center: "title", right: "timeGridDay,timeGridWeek,dayGridMonth,listWeek" }}
          buttonText={{ today: "Oggi", day: "Giorno", week: "Settimana", month: "Mese", list: "Agenda" }}
          events={fetchEvents} editable={can("write")} selectable={can("write")} longPressDelay={350} eventLongPressDelay={350} selectLongPressDelay={350} eventResizableFromStart={false} dayMaxEvents={isMobile ? 2 : 4}
          eventClick={(info) => setEdit(info.event.extendedProps)}
          select={(info) => can("write") && setCreate({ due_date: info.startStr.slice(0, 16), end_date: info.allDay ? undefined : info.endStr.slice(0, 16), all_day: info.allDay, type: "meeting", duration: Math.max(15, Math.round((info.end - info.start) / 60000)) })}
          eventDrop={onMove} eventResize={onMove}
          eventDidMount={(info) => { info.el.setAttribute("data-testid", `calendar-event-${info.event.id}`); info.el.title = `${info.event.title}${info.event.extendedProps.company_name ? " · " + info.event.extendedProps.company_name : ""}`; }} />
      </div>
      <FormDialog open={!!create} onOpenChange={(o) => !o && setCreate(null)} title="Nuovo evento" fields={ACTIVITY_FIELDS} endpoint="/activities" initial={createInitial} onSaved={() => { setCreate(null); refetch(); window.dispatchEvent(new Event("crm-refresh")); }} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica evento" fields={ACTIVITY_FIELDS} endpoint={edit ? `/activities/${edit.id}` : "/activities"} method="put"
        initial={editInitial} onSaved={() => { setEdit(null); refetch(); window.dispatchEvent(new Event("crm-refresh")); }} />
    </div>
  );
}
